import React, { useState, useEffect } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import "./StatItem.css";

/**
 * StatItem - Compact stat chip: tinted icon → value + label inline.
 * Same public API as before (icon/value/label/variant/onClick).
 */
function StatItem({ value, label, variant = "default", onClick, className = "" }) {
  return (
    <div
      className={`stat-tile ${onClick ? "stat-tile--clickable" : ""} ${className}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="stat-tile__body">
        <span className="stat-tile__value">{value}</span>
        <span className="stat-tile__label">{label}</span>
      </div>
    </div>
  );
}

const ROTATE_MS = 4000;

/**
 * StatItemGrid - Auto-rotating carousel of stat chips.
 * Desktop: 4 items visible. Mobile (≤768px): 1 item. Wraps around the full
 * set (handles any count, 3–12+). Auto-advances; pauses on hover/focus.
 * Small caret arrows + dots for manual control. Hidden when count fits in
 * one page. `columns` kept for API compat (ignored).
 */
export function StatItemGrid({ children, className = "" }) {
  const items = React.Children.toArray(children).filter(Boolean);
  const count = items.length;

  const [perPage, setPerPage] = useState(4);
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  // Slide direction for the enter animation: 1 = forward, -1 = backward.
  const [dir, setDir] = useState(1);

  // Responsive window size: 1 on mobile, 4 on desktop.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => {
      setPerPage(mq.matches ? 1 : 4);
      setPage(0);
    };
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const pageCount = Math.max(1, Math.ceil(count / perPage));
  const canRotate = pageCount > 1;

  // Auto-advance a full page at a time, wrapping around.
  useEffect(() => {
    if (!canRotate || paused) return undefined;
    const id = setInterval(() => {
      setDir(1);
      setPage((p) => (p + 1) % pageCount);
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [canRotate, paused, pageCount]);

  if (count === 0) return null;

  const safePage = page % pageCount;
  // Fill the row by wrapping, so a short last page still shows `perPage` items.
  const visibleCount = Math.min(perPage, count);
  const startIdx = (safePage * perPage) % count;
  const visible = Array.from(
    { length: visibleCount },
    (_, i) => items[(startIdx + i) % count],
  );

  const goPrev = () => {
    setDir(-1);
    setPage((p) => (p - 1 + pageCount) % pageCount);
  };
  const goNext = () => {
    setDir(1);
    setPage((p) => (p + 1) % pageCount);
  };

  return (
    <div
      className={`stat-carousel ${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      {canRotate && (
        <button
          type="button"
          className="stat-carousel__arrow"
          onClick={goPrev}
          aria-label="הקודם"
        >
          <CaretRight size={16} weight="bold" />
        </button>
      )}

      <div className="stat-carousel__viewport">
        <div
          className={`stat-carousel__track ${
            dir < 0 ? "stat-carousel__track--prev" : "stat-carousel__track--next"
          }`}
          key={safePage}
        >
          {visible.map((child, i) => (
            <div className="stat-carousel__slot" key={`slot-${i}`}>
              {child}
            </div>
          ))}
        </div>
      </div>

      {canRotate && (
        <button
          type="button"
          className="stat-carousel__arrow"
          onClick={goNext}
          aria-label="הבא"
        >
          <CaretLeft size={16} weight="bold" />
        </button>
      )}

      {canRotate && (
        <div className="stat-carousel__dots" role="tablist">
          {Array.from({ length: pageCount }, (_, i) => (
            <button
              type="button"
              key={`dot-${i}`}
              className={`stat-carousel__dot ${
                i === safePage ? "stat-carousel__dot--active" : ""
              }`}
              onClick={() => {
                setDir(i > safePage ? 1 : -1);
                setPage(i);
              }}
              aria-label={`עבור לעמוד ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default StatItem;
